import { useEffect, useState, useRef } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  TrendingUp,
  TrendingDown,
  Trophy,
  Target,
  Calendar as CalendarIcon,
  Camera,
  Upload,
  X,
  Check,
  Info,
  PlusCircle,
  BarChart3,
  Flag,
  Play,
  DollarSign,
  History,
  MessageCircle,
  Bug,
  Lightbulb,
  User,
  HelpCircle,
  Send,
  Rss,
  Users,
  Edit3,
  CalendarDays,
  ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";

// Safe text renderer - converts **bold** to <strong> safely
const SafeTextWithBold = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <>
      {parts.map((part, index) => 
        index % 2 === 1 ? (
          <strong key={`bold-${index}-${part.slice(0, 10)}`} className="text-stone-800">{part}</strong>
        ) : (
          <span key={`text-${index}-${part.slice(0, 10)}`}>{part}</span>
        )
      )}
    </>
  );
};
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "./ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Motivational messages for qualified players (3+ rounds)
const qualifiedMessages = [
  "Game is trending up",
  "Swing dialed in",
  "Finding your rhythm",
  "Confidence is showing",
  "Steady improvement"
];

// Get the handicap status message based on rounds count
// eighteenHoleCount: Full 18-hole rounds + paired 9-hole combos
// unpairedNines: Number of 9-hole rounds waiting for a pair (each = 0.5 toward a round)
const getHandicapStatusMessage = (eighteenHoleCount, unpairedNines, handicap) => {
  // Calculate effective rounds (18-hole equivalents + 0.5 for each unpaired 9)
  const effectiveRounds = eighteenHoleCount + (unpairedNines * 0.5);
  
  if (handicap !== null && eighteenHoleCount >= 3) {
    // Player is qualified - show rotating motivational message
    // Use effective rounds to determine which message to show (changes after each new round)
    const messageIndex = Math.floor(effectiveRounds) % qualifiedMessages.length;
    return qualifiedMessages[messageIndex];
  }
  
  // Not yet qualified - show rounds needed with .5 precision
  const roundsNeeded = 3 - effectiveRounds;
  
  if (roundsNeeded <= 0) return { text: "Qualified", isQualified: true };
  if (roundsNeeded >= 3) return { text: "3 rnds needed", isQualified: false };
  if (roundsNeeded === 2.5) return { text: "2.5 rnds needed", isQualified: false };
  if (roundsNeeded === 2) return { text: "2 rnds needed", isQualified: false };
  if (roundsNeeded === 1.5) return { text: "1.5 rnds needed", isQualified: false };
  if (roundsNeeded === 1) return { text: "1 more rnd needed", isQualified: false };
  if (roundsNeeded === 0.5) return { text: "0.5 rnd needed", isQualified: false };
  
  // Handle any other decimal values
  return { text: `${roundsNeeded} rnds needed`, isQualified: false };
};

const DashboardPage = ({ user, refreshProfile, profile, showOnboarding }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentScores, setRecentScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(null);
  
  // Active solo round state
  const [activeSoloRound, setActiveSoloRound] = useState(null);
  const [soloRoundCourse, setSoloRoundCourse] = useState(null);
  
  // Profile Photo state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  
  // Add Score state
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [front9, setFront9] = useState('');
  const [back9, setBack9] = useState('');
  const [scoreDate, setScoreDate] = useState(new Date());
  const [submittingScore, setSubmittingScore] = useState(false);
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [newCoursePar, setNewCoursePar] = useState('72');
  const [scoreEntryType, setScoreEntryType] = useState('half'); // 'half' for front/back, 'hole' for hole-by-hole
  const [holeScores, setHoleScores] = useState(Array(18).fill(''));
  
  // Scorecard Scanner state
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const scanInputRef = useRef(null);

  // Feedback state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('bug');
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Instruction content for different features
  const instructions = {
    addScore: {
      title: "Adding Your Score",
      icon: "🏌️",
      content: [
        "Track your rounds easily with multiple entry options:",
        "• **Total Only** - Just enter your final score for quick logging",
        "• **Hole-by-Hole** - Enter each hole for detailed stats",
        "• **Photo Scan** - Take a photo of your scorecard and let AI extract the scores",
        "",
        "Your scores help calculate your handicap after 3 rounds. The more you track, the more accurate your handicap becomes!"
      ],
      encouragement: "Every round is a step toward improvement. Let's get those scores logged! ⛳"
    },
    handicap: {
      title: "Understanding Your Handicap",
      icon: "📊",
      content: [
        "Your Handicap Index reflects your potential playing ability:",
        "• Calculated from your **best 8 of last 20** rounds",
        "• Lower handicap = better player",
        "• Updates automatically as you log more rounds",
        "",
        "Need **3+ rounds** to establish your handicap. The system uses the USGA formula for accuracy."
      ],
      encouragement: "Keep playing and tracking - watch your handicap improve over time! 🎯"
    },
    matchPlay: {
      title: "Match Play Leagues",
      icon: "🏆",
      content: [
        "Compete in exciting team-based match play:",
        "• Create or join leagues with 2-person teams",
        "• **14-point scoring** system per match",
        "• Automatic handicap adjustments",
        "• Weekly schedules with round-robin matchups",
        "",
        "Add guest players or link existing Par Seeker users. Substitutes can fill in with 0 handicap."
      ],
      encouragement: "Golf is better with friends. Start a league and make every round count! 🤝"
    },
    stats: {
      title: "Your Golf Statistics",
      icon: "📈",
      content: [
        "Track your progress with detailed stats:",
        "• **Total Rounds** - See how active you've been",
        "• **Average Score** - Your typical performance",
        "• **Best/Worst** - Track your range",
        "• **Score Trend** - Visual progress over time",
        "",
        "The more rounds you log, the more insights you unlock!"
      ],
      encouragement: "Data drives improvement. Your best round is still ahead! 🌟"
    }
  };

  const InstructionModal = ({ type, onClose }) => {
    const info = instructions[type];
    if (!info) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md animate-fade-in">
          <CardHeader className="text-center pb-2">
            <div className="text-4xl mb-2">{info.icon}</div>
            <CardTitle className="text-xl">{info.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-stone-600 space-y-2">
              {info.content.map((line, i) => (
                <p key={`modal-info-line-${i}`}>
                  <SafeTextWithBold text={line} />
                </p>
              ))}
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800 font-medium">{info.encouragement}</p>
            </div>
          </CardContent>
          <div className="px-6 pb-6">
            <Button onClick={onClose} className="w-full btn-primary">
              Got it, let's go!
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  // Submit feedback handler
  const handleSubmitFeedback = async () => {
    if (!feedbackDescription.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/feedback`, {
        issue_type: feedbackType,
        description: feedbackDescription,
        page_url: window.location.href,
        device_info: navigator.userAgent
      });
      
      toast.success("Thank you! Your feedback has been submitted.");
      setShowFeedbackModal(false);
      setFeedbackDescription('');
      setFeedbackType('bug');
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    // Don't fetch data while onboarding is showing
    if (showOnboarding) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        const [statsRes, coursesRes, scoresRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/courses`),
          axios.get(`${API}/scores?limit=5`)
        ]);
        setStats(statsRes.data);
        setCourses(coursesRes.data);
        setRecentScores(scoresRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        // Only show error toast if it's not a 404 (new user with no data)
        if (error.response?.status !== 404) {
          // Silently handle errors for new users - they just have no data yet
          if (error.response?.status === 401) {
            // Auth issue - will be handled by auth context
          } else {
            toast.error("Failed to load dashboard data");
          }
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [showOnboarding]);

  // Check for active solo round
  useEffect(() => {
    const checkActiveSoloRound = async () => {
      try {
        const response = await axios.get(`${API}/scores/draft`);
        if (response.data && response.data.hole_scores && response.data.hole_scores.some(s => s !== null)) {
          setActiveSoloRound(response.data);
          
          // Fetch course info if we have course_id
          if (response.data.course_id) {
            try {
              const courseRes = await axios.get(`${API}/courses/${response.data.course_id}`);
              setSoloRoundCourse(courseRes.data);
            } catch (e) {
              // Course not found
            }
          }
        }
      } catch (err) {
        // No active draft
      }
    };
    checkActiveSoloRound();
  }, []);

  const getScoreClass = (score, par) => {
    const diff = score - par;
    if (diff < 0) return 'score-under';
    if (diff === 0) return 'score-par';
    if (diff <= 5) return 'score-over';
    return 'score-bad';
  };

  // Profile photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/profile/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Profile photo updated!');
      await refreshProfile();
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="dark-spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 pt-0 animate-fade-in" data-testid="dashboard-page">
      
      {/* Active Solo Round Banner */}
      {activeSoloRound && (
        <div 
          className="dark-card overflow-hidden cursor-pointer hover:shadow-xl transition-all mb-4 border border-green-500/50"
          onClick={() => navigate(`/add-score?resume=solo${activeSoloRound.draft_id ? `&draft=${encodeURIComponent(activeSoloRound.draft_id)}` : ''}`)}
          data-testid="active-solo-round-banner"
        >
          <div className="bg-gradient-to-r from-green-600/20 via-green-700/20 to-emerald-800/20 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Play className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 bg-yellow-400 text-yellow-900 rounded font-bold">IN PROGRESS</span>
                  </div>
                  <p className="text-[var(--ps-text-primary)] font-semibold mt-0.5">
                    {soloRoundCourse?.name || 'Solo Round'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-400">
                    {activeSoloRound.hole_scores?.filter(s => s !== null).length || 0}
                    <span className="text-sm text-[var(--ps-text-muted)]">/18</span>
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)]">holes</p>
                </div>
                <ChevronRight className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {stats?.total_rounds === 0 ? (
        <div className="dark-card p-8 text-center">
          <Flag className="w-12 h-12 mx-auto mb-4 text-[var(--ps-text-muted)]" />
          <h3 className="text-xl font-semibold text-[var(--ps-text-primary)] mb-2">No rounds yet</h3>
          <p className="text-[var(--ps-text-secondary)] mb-6">Start tracking your golf scores to see your stats here.</p>
          <NavLink to="/add-score">
            <span className="dark-btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full" data-testid="add-first-score-btn">
              <PlusCircle className="w-4 h-4" />
              Add Your First Score
            </span>
          </NavLink>
        </div>
      ) : (
        <>
          {/* Handicap Card - Dark Theme */}
          <NavLink to="/add-score" className="block mb-4">
            <div className="dark-card overflow-hidden cursor-pointer hover:border-[var(--ps-primary)] transition-all group" data-testid="handicap-card">
              <div className="bg-gradient-to-br from-[var(--ps-primary)] via-green-600 to-green-700 p-5 flex justify-between">
                <div className="flex flex-col justify-between">
                  <div>
                    <p className="text-white/70 text-sm font-bold uppercase tracking-wider mb-0.5">Official</p>
                    <h2 className="text-white text-xl font-black tracking-tight">HANDICAP INDEX</h2>
                  </div>
                  <p className="text-white/60 text-xs mt-1 flex items-center gap-1">
                    <PlusCircle className="w-3 h-3" />
                    Tap to add score
                  </p>
                </div>
                <div className="text-right flex flex-col justify-between">
                  <p className="text-5xl font-black text-white tracking-tight" data-testid="handicap-value">
                    {stats?.handicap !== null ? stats?.handicap?.toFixed(1) : '--'}
                  </p>
                  <p 
                    className={`text-sm font-semibold ${stats?.handicap !== null && stats?.handicap !== undefined ? 'text-white' : 'text-orange-400'}`}
                    data-testid="handicap-status-message"
                  >
                    {stats?.handicap !== null && stats?.handicap !== undefined ? 'Qualified' : 'Qualifying'}
                  </p>
                </div>
              </div>
              <div className="flex">
                <div className="flex-1 h-1.5 bg-amber-500"></div>
                <div className="flex-1 h-1.5 bg-white"></div>
                <div className="flex-1 h-1.5 bg-amber-500"></div>
                <div className="flex-1 h-1.5 bg-white"></div>
                <div className="flex-1 h-1.5 bg-amber-500"></div>
              </div>
            </div>
          </NavLink>

          {/* Stats Grid - Dark - Clickable to History */}
          <div className="dark-stats-grid mb-6" data-testid="stats-row">
            <div 
              className="dark-stat-card cursor-pointer hover:border-[var(--ps-primary)] transition-all" 
              data-testid="total-rounds-card"
              onClick={() => navigate('/history')}
            >
              <p className="dark-stat-label">Rounds</p>
              <p className="dark-stat-value" data-testid="total-rounds-value">
                {(() => {
                  const effectiveRounds = (stats?.eighteen_hole_count || 0) + ((stats?.unpaired_nine_count || 0) * 0.5);
                  // Show decimal only if there's a .5
                  return effectiveRounds % 1 === 0 ? effectiveRounds : effectiveRounds.toFixed(1);
                })()}
              </p>
            </div>
            <div 
              className="dark-stat-card cursor-pointer hover:border-[var(--ps-primary)] transition-all" 
              data-testid="avg-score-card"
              onClick={() => navigate('/history')}
            >
              <p className="dark-stat-label">Average</p>
              <p className="dark-stat-value" data-testid="avg-score-value">{stats?.average_score?.toFixed(1) || '--'}</p>
            </div>
            <div 
              className="dark-stat-card cursor-pointer hover:border-[var(--ps-success)] transition-all" 
              style={{borderColor: 'var(--ps-success)'}} 
              data-testid="best-score-card"
              onClick={() => navigate('/history?sort=best')}
            >
              <p className="dark-stat-label" style={{color: 'var(--ps-success)'}}>Best</p>
              <p className="dark-stat-value" style={{color: 'var(--ps-success)'}} data-testid="best-score-value">{stats?.best_score || '--'}</p>
            </div>
            <div 
              className="dark-stat-card cursor-pointer hover:border-[var(--ps-error)] transition-all" 
              style={{borderColor: 'var(--ps-error)'}} 
              data-testid="worst-score-card"
              onClick={() => navigate('/history?sort=worst')}
            >
              <p className="dark-stat-label" style={{color: 'var(--ps-error)'}}>Worst</p>
              <p className="dark-stat-value" style={{color: 'var(--ps-error)'}} data-testid="worst-score-value">{stats?.worst_score || '--'}</p>
            </div>
          </div>
        </>
      )}

      {/* Quick Actions - Premium Card Style Grid with progressive sizing */}
      <div className="ps-dashboard-actions grid grid-cols-2 gap-3 mb-6">
        {/* PLAY Button - Green with green glow - Largest */}
        <button 
          onClick={() => navigate('/add-score')} 
          data-testid="play-btn"
          className="relative p-5 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #22c55e 0%, #15803d 100%)',
            boxShadow: '0 0 20px rgba(34, 197, 94, 0.6), 0 0 40px rgba(34, 197, 94, 0.3), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(34, 197, 94, 0.7)'
          }}
        >
          {/* Light overlay for glossy effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-[#166534] flex items-center justify-center mb-4 border-2 border-green-400/50 shadow-[inset_0_2px_4px_rgba(255,255,255,0.3)]">
              <Play className="w-6 h-6 text-green-300" fill="currentColor" />
            </div>
            <h3 className="text-white font-bold text-xl tracking-wide">PLAY</h3>
            <p className="text-green-100/80 text-sm mt-1">Start a new game</p>
          </div>
          <ChevronRight className="absolute bottom-4 right-4 w-5 h-5 text-green-200/70 z-10" />
        </button>

        {/* GAMES Button - Orange/Amber with orange glow - Largest */}
        <button 
          onClick={() => navigate('/tournaments')} 
          data-testid="games-btn"
          className="relative p-5 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #d97706 0%, #b45309 100%)',
            boxShadow: '0 0 20px rgba(217, 119, 6, 0.6), 0 0 40px rgba(217, 119, 6, 0.3), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(251, 191, 36, 0.7)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/25 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <Trophy className="w-10 h-10 text-amber-200 mb-4 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" />
            <h3 className="text-white font-bold text-xl tracking-wide">GAMES</h3>
            <p className="text-amber-100/80 text-sm mt-1">View your games</p>
          </div>
          <ChevronRight className="absolute bottom-4 right-4 w-5 h-5 text-amber-200/70 z-10" />
        </button>

        {/* SETTLE Button - Dark with green accents - Medium-Large */}
        <button 
          onClick={() => navigate('/settlements')} 
          data-testid="settle-btn"
          className="relative p-4 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #1a2e1a 0%, #0f1f0f 100%)',
            boxShadow: '0 0 15px rgba(34, 197, 94, 0.4), 0 0 30px rgba(34, 197, 94, 0.2), 0 4px 15px rgba(0,0,0,0.4)',
            border: '2px solid rgba(34, 197, 94, 0.5)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/8 via-transparent to-black/15 pointer-events-none" />
          <div className="relative z-10">
            <div className="w-11 h-11 rounded-full border-2 border-green-500/60 flex items-center justify-center mb-3 bg-transparent shadow-[inset_0_1px_3px_rgba(255,255,255,0.15)]">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <h3 className="text-green-400 font-bold text-lg tracking-wide">SETTLE</h3>
            <p className="text-green-300/70 text-sm mt-1">Create a settlement</p>
          </div>
          <ChevronRight className="absolute bottom-4 right-4 w-5 h-5 text-green-500/60 z-10" />
        </button>

        {/* CHAT Button - Blue with blue glow - Medium-Large */}
        <button 
          onClick={() => navigate('/chat')} 
          data-testid="chat-btn"
          className="relative p-4 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #1e40af 0%, #1e3a8a 100%)',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.25), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(59, 130, 246, 0.6)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/15 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <MessageCircle className="w-9 h-9 text-blue-300 mb-3 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" />
            <h3 className="text-white font-bold text-lg tracking-wide">CHAT</h3>
            <p className="text-blue-200/80 text-sm mt-1">Talk with friends</p>
          </div>
          <ChevronRight className="absolute bottom-4 right-4 w-5 h-5 text-blue-300/60 z-10" />
        </button>

        {/* FEED Button - Purple with magenta/pink glow - Medium */}
        <button 
          onClick={() => navigate('/feed')} 
          data-testid="feed-btn"
          className="relative p-3.5 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #7c3aed 0%, #5b21b6 100%)',
            boxShadow: '0 0 20px rgba(168, 85, 247, 0.5), 0 0 40px rgba(236, 72, 153, 0.3), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(168, 85, 247, 0.6)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <Rss className="w-8 h-8 text-purple-300 mb-2.5 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" />
            <h3 className="text-white font-bold text-base tracking-wide">FEED</h3>
            <p className="text-purple-200/80 text-xs mt-1">See latest activity</p>
          </div>
          <ChevronRight className="absolute bottom-3 right-3 w-4 h-4 text-purple-300/60 z-10" />
        </button>

        {/* MEMBERS Button - Teal/Blue-green with cyan glow - Medium */}
        <button 
          onClick={() => navigate('/buddies')} 
          data-testid="members-btn"
          className="relative p-3.5 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #0d9488 0%, #0f766e 100%)',
            boxShadow: '0 0 20px rgba(20, 184, 166, 0.5), 0 0 40px rgba(6, 182, 212, 0.3), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(45, 212, 191, 0.6)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <Users className="w-8 h-8 text-teal-300 mb-2.5 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" />
            <h3 className="text-white font-bold text-base tracking-wide">MEMBERS</h3>
            <p className="text-teal-100/80 text-xs mt-1">Manage members</p>
          </div>
          <ChevronRight className="absolute bottom-3 right-3 w-4 h-4 text-teal-300/60 z-10" />
        </button>

        {/* POST Button - Cyan/Teal with cyan glow - Smaller */}
        <button 
          onClick={() => navigate('/feed?action=post')} 
          data-testid="post-btn"
          className="relative p-3 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #0891b2 0%, #0e7490 100%)',
            boxShadow: '0 0 20px rgba(6, 182, 212, 0.5), 0 0 40px rgba(34, 211, 238, 0.3), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(34, 211, 238, 0.6)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <Edit3 className="w-7 h-7 text-cyan-300 mb-2 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" />
            <h3 className="text-white font-bold text-sm tracking-wide">POST</h3>
            <p className="text-cyan-100/80 text-xs mt-0.5">Share an update</p>
          </div>
          <ChevronRight className="absolute bottom-2.5 right-2.5 w-4 h-4 text-cyan-300/60 z-10" />
        </button>

        {/* BOOK Button - Yellow/Gold with yellow glow - Smaller */}
        <button 
          onClick={() => navigate('/book')} 
          data-testid="book-btn"
          className="relative p-3 rounded-2xl text-left transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #eab308 0%, #ca8a04 100%)',
            boxShadow: '0 0 20px rgba(234, 179, 8, 0.6), 0 0 40px rgba(250, 204, 21, 0.3), 0 4px 15px rgba(0,0,0,0.3)',
            border: '2px solid rgba(250, 204, 21, 0.7)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-black/10 pointer-events-none" />
          <div className="relative z-10">
            <CalendarDays className="w-7 h-7 text-yellow-100 mb-2 drop-shadow-[0_2px_2px_rgba(0,0,0,0.2)]" />
            <h3 className="text-white font-bold text-sm tracking-wide">BOOK</h3>
            <p className="text-yellow-100/80 text-xs mt-0.5">Schedule a game</p>
          </div>
          <ChevronRight className="absolute bottom-2.5 right-2.5 w-4 h-4 text-yellow-200/60 z-10" />
        </button>
      </div>

      {/* History Link */}
      <button 
        className="w-full dark-card p-4 flex items-center justify-center gap-3 hover:border-[var(--ps-primary)] transition-all"
        onClick={() => navigate('/history')}
      >
        <History className="w-5 h-5 text-[var(--ps-primary)]" />
        <span className="font-semibold text-[var(--ps-text-primary)]">View Score History</span>
      </button>

      {/* Instruction Modal */}
      {showInstructions && (
        <InstructionModal type={showInstructions} onClose={() => setShowInstructions(null)} />
      )}

      {/* Feedback Banner - Dark */}
      <div className="mt-8">
        <button
          onClick={() => navigate('/support')}
          className="w-full dark-card p-4 flex items-center justify-center gap-3 hover:border-[var(--ps-primary)] transition-all group"
          data-testid="open-feedback-btn"
        >
          <img 
            src="/images/golf-ball.png" 
            alt="Feedback" 
            className="w-8 h-8 object-contain opacity-70 group-hover:opacity-100 transition-opacity"
          />
          <span className="text-[var(--ps-text-secondary)] group-hover:text-[var(--ps-text-primary)] transition-colors">
            Found a bug? Have an idea? <span className="text-[var(--ps-primary)]">Tell us!</span>
          </span>
        </button>
      </div>

      {/* Feedback Modal - Dark Theme */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="sm:max-w-md" style={{backgroundColor: 'var(--ps-bg-surface)', borderColor: 'var(--ps-border)'}}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl text-[var(--ps-text-primary)]">
              <img 
                src="/images/golf-ball.png" 
                alt="Feedback" 
                className="w-10 h-10 object-contain"
              />
              Report an Issue
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Issue Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">What type of issue?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFeedbackType('bug')}
                  className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                    feedbackType === 'bug' 
                      ? 'border-[var(--ps-primary)] bg-[var(--ps-primary-light)] text-[var(--ps-primary)]' 
                      : 'border-[var(--ps-border)] text-[var(--ps-text-secondary)] hover:border-[var(--ps-border-light)]'
                  }`}
                  data-testid="feedback-type-bug"
                >
                  <Bug className="w-5 h-5" />
                  <span className="font-medium">Bug</span>
                </button>
                <button
                  onClick={() => setFeedbackType('feature')}
                  className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                    feedbackType === 'feature' 
                      ? 'border-[var(--ps-primary)] bg-[var(--ps-primary-light)] text-[var(--ps-primary)]' 
                      : 'border-[var(--ps-border)] text-[var(--ps-text-secondary)] hover:border-[var(--ps-border-light)]'
                  }`}
                  data-testid="feedback-type-feature"
                >
                  <Lightbulb className="w-5 h-5" />
                  <span className="font-medium">Feature</span>
                </button>
                <button
                  onClick={() => setFeedbackType('account')}
                  className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                    feedbackType === 'account' 
                      ? 'border-[var(--ps-primary)] bg-[var(--ps-primary-light)] text-[var(--ps-primary)]' 
                      : 'border-[var(--ps-border)] text-[var(--ps-text-secondary)] hover:border-[var(--ps-border-light)]'
                  }`}
                  data-testid="feedback-type-account"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Account</span>
                </button>
                <button
                  onClick={() => setFeedbackType('other')}
                  className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                    feedbackType === 'other' 
                      ? 'border-[var(--ps-primary)] bg-[var(--ps-primary-light)] text-[var(--ps-primary)]' 
                      : 'border-[var(--ps-border)] text-[var(--ps-text-secondary)] hover:border-[var(--ps-border-light)]'
                  }`}
                  data-testid="feedback-type-other"
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="font-medium">Other</span>
                </button>
              </div>
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[var(--ps-text-secondary)]">Describe the issue</Label>
              <textarea
                value={feedbackDescription}
                onChange={(e) => setFeedbackDescription(e.target.value)}
                placeholder="Please describe what happened or what you'd like to see..."
                className="dark-input w-full min-h-[120px] p-3 text-sm resize-none"
                data-testid="feedback-description"
              />
            </div>
            
            {/* Submit Button */}
            <Button
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback || !feedbackDescription.trim()}
              className="dark-btn-primary w-full h-12 text-lg font-bold"
              data-testid="submit-feedback-btn"
            >
              {submittingFeedback ? (
                <div className="dark-spinner w-5 h-5" />
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
            
            <p className="text-xs text-[var(--ps-text-muted)] text-center">
              We read every piece of feedback and will investigate all issues.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default DashboardPage;
